import { flatten } from 'lodash';
import {
  type Descendant,
  Element as SlateElement,
  Node as SlateNode,
  Text as SlateText,
} from 'slate';
import { jsx } from 'slate-hyperscript';

import { PROMPT_INSTRUCTION_TYPE } from 'src/data/generation';
import {
  createScoringMethod,
  createTaxonomyScoringMethod,
  DEFAULT_SCORING_METHOD,
  type ScoringMethod,
} from 'src/data/scoring';
import { generateVariationInstanceId } from 'src/data/variation';
import { captureMessage } from 'src/lib/sentry';
import { containsOnlyNewlines, countNewlines } from 'src/lib/string';

import {
  DEFAULT_SLATE_VALUE,
  EMPTY_PARAGRAPH_HTML,
  SLATE_TEXT_FORMATS,
  SLATE_VOID_ELEMENT,
  type SlateTextFormat,
} from './const';
import {
  createBlockNode,
  createBulletedListNode,
  createListItemNode,
  createTextNode,
  createTextParagraph,
} from './templateCreators';
import { type CustomSlateElement, type SlateValue } from './types';

interface TextToSlateOptions {
  defaultTextFormat?: SlateTextFormat;
}

const isDDVariationElement = (element: HTMLElement) =>
  element.dataset && (element.dataset.variationId || element.dataset.savedVariationId);

const parseScoringMethod = (dataset: DOMStringMap): ScoringMethod => {
  if (dataset.scoringMethod) {
    return createScoringMethod({
      type: dataset.scoringMethod,
      id: dataset.scoringMethodId,
      name: decodeURI(dataset.scoringMethodName ?? ''),
    } as ScoringMethod);
  }

  // Previous iteration of scoring required just "contentTaxonomyId"
  if (dataset.contentTaxonomyId) {
    return createTaxonomyScoringMethod(dataset.contentTaxonomyId);
  }

  return DEFAULT_SCORING_METHOD;
};

const parseSecondaryScoringMethodName = (dataset: DOMStringMap) => {
  if (dataset.secondaryScoringMethodName) {
    return decodeURI(dataset.secondaryScoringMethodName);
  }
};

const parseCampaignKeywords = (dataset: DOMStringMap) => {
  const { campaignKeywords } = dataset;
  if (campaignKeywords) {
    const decoded = decodeURI(campaignKeywords);
    try {
      return JSON.parse(decoded);
    } catch (error) {
      captureMessage('Error parsing campaign keywords', {
        level: 'warning',
        extra: {
          error: String(error),
          source: 'deserialize.ts -> parseCampaignKeywords',
          value: decoded,
        },
      });
    }
  }
};

const parseBenchmarkConfig = (dataset: DOMStringMap) => {
  if (dataset.benchmarkConfigUserGoal) {
    return {
      comparisonSet: {
        userGoal: dataset.benchmarkConfigUserGoal,
        name: decodeURIComponent(dataset.benchmarkConfigComparisonSetName ?? ''),
      },
      account: {
        id: dataset.benchmarkConfigAccountId,
        name: decodeURIComponent(dataset.benchmarkConfigAccountName ?? ''),
        channel: decodeURIComponent(dataset.benchmarkConfigAccountChannel ?? ''),
      },
    };
  }

  // Previous iteration of scoring required just "contentTaxonomyId"
  if (dataset.contentTaxonomyId) {
    return createTaxonomyScoringMethod(dataset.contentTaxonomyId);
  }

  return DEFAULT_SCORING_METHOD;
};

// Older saved documents hold the string "undefined" or "null" for a missing value.
const readVariationDataset = (element: HTMLElement): DOMStringMap =>
  Object.fromEntries(
    Object.entries(element.dataset).filter(([, value]) => value !== 'undefined' && value !== 'null')
  );

const parseDDVariation = (element: HTMLElement, children: Descendant[]) => {
  const dataset = readVariationDataset(element);
  const isSaved = Boolean(dataset.savedVariationId);
  const variationId = isSaved ? dataset.savedVariationId : dataset.variationId;

  return jsx(
    'element',
    {
      type: SLATE_TEXT_FORMATS.DD_VARIATION,
      variation: {
        id: variationId,
        assetGroupId: dataset.assetGroupId,
        assetGroupType: dataset.assetGroupType,
        instanceId: generateVariationInstanceId(variationId ?? ''),
        score: dataset.variationScore,
        assetType: dataset.assetType,
        generationTool: dataset.generationTool,
        language: dataset.language,
        isSaved,
        isScoreLocked: dataset.scoreLocked != null,
        scoringMethod: parseScoringMethod(dataset),
        secondaryScoringMethod: dataset.secondaryScoringMethodName
          ? { name: parseSecondaryScoringMethodName(dataset) }
          : null,
        campaignKeywords: parseCampaignKeywords(dataset),
        benchmarkConfig: parseBenchmarkConfig(dataset),
        imageBlob: dataset.imageBlob,
        lastUpdateTime: dataset.lastUpdateTime,
      },
    },
    children
  );
};

interface TextMarks {
  bold?: boolean;
  italic?: boolean;
  underline?: boolean;
  strikethrough?: boolean;
}

// Parse text attributes for text nodes
const parseTextAttribute = (el: HTMLElement): TextMarks => {
  const result: TextMarks = {};

  const nodeName = el.nodeName.toLowerCase();
  if (['strong', 'b'].includes(nodeName) || Number(el.style.fontWeight) > 400) {
    // GoogleDocs uses 'b' as wrapper even for non-bold text
    if (el.style.fontWeight !== 'normal') {
      result.bold = true;
    }
  }

  if (['em', 'i'].includes(nodeName) || el.style.fontStyle === 'italic') {
    result.italic = true;
  }

  if (nodeName === 'u') {
    result.underline = true;
  }

  if (nodeName === 's') {
    result.strikethrough = true;
  }

  return result;
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const deserialize = (el: Node, markAttributes: Record<string, unknown> = {}): any => {
  if (el.nodeType === Node.TEXT_NODE) {
    return jsx('text', markAttributes, el.textContent);
  }
  if (el.nodeType !== Node.ELEMENT_NODE) {
    return null;
  }

  const element = el as HTMLElement;

  const nodeAttributes = {
    ...markAttributes,
    ...parseTextAttribute(element),
  };

  const children = flatten(
    Array.from(element.childNodes).map((node) => deserialize(node, nodeAttributes))
  );

  if (children.length === 0) {
    children.push(jsx('text', nodeAttributes, ''));
  }

  const commonAttributes: { align?: string } = {};
  if (element.style.textAlign) {
    commonAttributes.align = element.style.textAlign;
  }

  switch (element.nodeName.toLowerCase()) {
    case 'body': {
      const filteredChildren = hasElementNodes(children) ? skipBlankTextNodes(children) : children;
      return jsx('fragment', {}, filteredChildren);
    }

    case 'h1':
      return jsx(
        'element',
        {
          ...commonAttributes,
          type: SLATE_TEXT_FORMATS.HEADING_ONE,
        },
        children
      );

    case 'h2':
      return jsx(
        'element',
        {
          ...commonAttributes,
          type: SLATE_TEXT_FORMATS.HEADING_TWO,
        },
        children
      );

    case 'h3':
      return jsx(
        'element',
        {
          ...commonAttributes,
          type: SLATE_TEXT_FORMATS.HEADING_THREE,
        },
        children
      );

    case 'h4':
      return jsx(
        'element',
        {
          ...commonAttributes,
          type: SLATE_TEXT_FORMATS.HEADING_FOUR,
        },
        children
      );

    case 'h5':
      return jsx(
        'element',
        {
          ...commonAttributes,
          type: SLATE_TEXT_FORMATS.HEADING_FIVE,
        },
        children
      );

    case 'h6':
      return jsx(
        'element',
        {
          ...commonAttributes,
          type: SLATE_TEXT_FORMATS.HEADING_SIX,
        },
        children
      );

    case 'ol': {
      const items = skipTextNodes(children) || [createListItemNode('')];
      return jsx(
        'element',
        {
          ...commonAttributes,
          type: SLATE_TEXT_FORMATS.NUMBERED_LIST,
        },
        items
      );
    }

    case 'ul': {
      const items = skipTextNodes(children) || [createListItemNode('')];
      return jsx(
        'element',
        {
          ...commonAttributes,
          type: SLATE_TEXT_FORMATS.BULLETED_LIST,
        },
        items
      );
    }

    case 'li': {
      const filteredChildren = hasElementNodes(children) ? skipBlankTextNodes(children) : children;
      return jsx(
        'element',
        {
          ...commonAttributes,
          type: SLATE_TEXT_FORMATS.LIST_ITEM,
        },
        filteredChildren
      );
    }

    case 'br':
      return '\n';

    case 'blockquote': {
      // Sometimes pasted content has empty text nodes inside blockquote - take only element nodes in this case
      const items = hasElementNodes(children) ? skipTextNodes(children) : children;

      return jsx(
        'element',
        {
          ...commonAttributes,
          type: SLATE_TEXT_FORMATS.BLOCK_QUOTE,
        },
        items
      );
    }

    case 'div':
      if (element.dataset && element.dataset.divider) {
        return jsx(
          'element',
          {
            ...commonAttributes,
            type: SLATE_VOID_ELEMENT.DIVIDER,
          },
          [{ text: '' }]
        );
      }
      if (isDDVariationElement(element)) {
        return parseDDVariation(element, children);
      }
      return children;

    case 'p':
      return jsx(
        'element',
        {
          ...commonAttributes,
          type: SLATE_TEXT_FORMATS.PARAGRAPH,
        },
        children
      );

    case 'a':
      if (element.getAttribute('data-citation-id')) {
        return jsx(
          'element',
          {
            ...commonAttributes,
            type: SLATE_TEXT_FORMATS.LINK,
            url: element.getAttribute('href'),
            citationId: element.getAttribute('data-citation-id'),
          },
          children
        );
      }

      return jsx(
        'element',
        {
          ...commonAttributes,
          type: SLATE_TEXT_FORMATS.LINK,
          url: element.getAttribute('href'),
        },
        children
      );

    case 'img':
      return jsx(
        'element',
        {
          ...commonAttributes,
          type: SLATE_TEXT_FORMATS.IMG,
          src: element.getAttribute('src'),
        },
        children
      );

    case 'span':
      if (element.hasAttribute('data-bank-message')) {
        return jsx(
          'element',
          {
            ...commonAttributes,
            attributes: element.attributes,
            bankMessageId: element.getAttribute('data-bank-message-id'),
            bankMessageBody: element.getAttribute('data-bank-message-body'),
            tagName: element.getAttribute('data-bank-message-tag-name') || '',
            type: SLATE_TEXT_FORMATS.BANK_MESSAGE,
          },
          children
        );
      } else if (element.hasAttribute('data-prompt-image')) {
        return jsx(
          'element',
          {
            ...commonAttributes,
            id: element.getAttribute('data-prompt-image-id'),
            tooltip: element.getAttribute('data-tooltip') || '',
            type: SLATE_TEXT_FORMATS.PROMPT_IMAGE,
          },
          children
        );
      } else if (element.hasAttribute('data-prompt-document')) {
        return jsx(
          'element',
          {
            ...commonAttributes,
            id: element.getAttribute('data-prompt-document-id'),
            tooltip: element.getAttribute('data-tooltip') || '',
            type: SLATE_TEXT_FORMATS.PROMPT_DOCUMENT,
          },
          children
        );
      } else if (element.hasAttribute('data-prompt-video')) {
        return jsx(
          'element',
          {
            ...commonAttributes,
            id: element.getAttribute('data-id'),
            type: SLATE_TEXT_FORMATS.PROMPT_VIDEO,
            tooltip: element.getAttribute('data-tooltip') || undefined,
          },
          children
        );
      } else if (element.hasAttribute('data-prompt-audio')) {
        return jsx(
          'element',
          {
            ...commonAttributes,
            id: element.getAttribute('data-id'),
            type: SLATE_TEXT_FORMATS.PROMPT_AUDIO,
            tooltip: element.getAttribute('data-tooltip') || undefined,
          },
          children
        );
      } else if (element.hasAttribute('data-prompt-tag')) {
        return jsx(
          'element',
          {
            ...commonAttributes,
            attributes: element.attributes,
            type: SLATE_TEXT_FORMATS.PROMPT_TAG,
          },
          children
        );
      } else if (element.hasAttribute('data-product-description')) {
        return jsx(
          'element',
          {
            ...commonAttributes,
            attributes: element.attributes,
            title: element.getAttribute('data-product-description-title'),
            article: element.getAttribute('data-product-description-article'),
            type: SLATE_TEXT_FORMATS.PRODUCT_DESCRIPTION,
          },
          children
        );
      } else if (element.hasAttribute('data-prompt-variable')) {
        return jsx(
          'element',
          {
            ...commonAttributes,
            attributes: element.attributes,
            title: element.getAttribute('data-prompt-variable-title'),
            info: element.getAttribute('data-prompt-variable-info'),
            infoTooltip: element.getAttribute('data-prompt-variable-info-tooltip'),
            tooltipTitle: element.getAttribute('data-prompt-variable-tooltipTitle'),
            tooltipDescription: element.getAttribute('data-prompt-variable-tooltipDescription'),
            type: SLATE_TEXT_FORMATS.PROMPT_VARIABLE,
          },
          children
        );
      } else if (element.hasAttribute('data-resource-url')) {
        return jsx(
          'element',
          {
            ...commonAttributes,
            attributes: element.attributes,
            resourceUrl: element.getAttribute('data-resource-url-text'),
            tagName: element.getAttribute('data-resource-url-tag-name') || '',
            type: SLATE_TEXT_FORMATS.RESOURCE_URL,
          },
          children
        );
      } else if (element.hasAttribute('data-prompt-instruction')) {
        return [
          jsx('text', { text: '' }),
          jsx(
            'element',
            {
              ...commonAttributes,
              type: SLATE_TEXT_FORMATS.PROMPT_INSTRUCTION,
              data: parseInstructionData(element),
            },
            children
          ),
          jsx('text', { text: '' }),
        ];
      }
      return children;

    default:
      return children;
  }
};

const hasElementNodes = (nodes: Descendant[]) => nodes.some((node) => SlateElement.isElement(node));

const skipTextNodes = (nodes: Descendant[]) => nodes.filter((node) => !SlateText.isText(node));

const skipBlankTextNodes = (nodes: Descendant[]) =>
  nodes.filter((node) => !SlateText.isText(node) || SlateNode.string(node).trim() !== '');

export const htmlToSlate = (html = EMPTY_PARAGRAPH_HTML): SlateValue => {
  if (!html) {
    return DEFAULT_SLATE_VALUE;
  }

  const { body } = new DOMParser().parseFromString(html, 'text/html');
  const slateValue = deserialize(body);
  if (slateValue && slateValue.length > 0) {
    return normalizeSlateValue(slateValue) as SlateValue;
  }
  return DEFAULT_SLATE_VALUE;
};

const normalizeSlateValue = (value: Descendant[]) => {
  return value.flatMap((node) => {
    // convert all top-level \n text nodes to paragraphs
    if (SlateText.isText(node) && containsOnlyNewlines(node.text)) {
      const newlineCount = countNewlines(node.text);
      return Array.from({ length: newlineCount }).map(() => createTextParagraph(''));
    }
    return node;
  });
};

export const textToSlate = (text?: string | null, options?: TextToSlateOptions) => {
  if (!text) {
    return DEFAULT_SLATE_VALUE;
  }

  const lines = text.split('\n');
  const parsedLines = lines.map((line) => parseTextLine(line, options));
  return wrapListItems(parsedLines);
};

const wrapListItems = (parsedLines: CustomSlateElement[]) => {
  const result: CustomSlateElement[] = [];
  let currentList: CustomSlateElement | null = null;
  parsedLines.forEach((line) => {
    if (line.type === SLATE_TEXT_FORMATS.LIST_ITEM) {
      if (!currentList) {
        currentList = createBulletedListNode([]);
        result.push(currentList);
      }
      currentList.children.push(line);
    } else {
      currentList = null;
      result.push(line);
    }
  });
  return result;
};

const parseTextLine = (line: string, options?: TextToSlateOptions): CustomSlateElement => {
  if (isListItemText(line)) {
    const text = parseListItemText(line);
    return createListItemNode(text);
  }

  return options?.defaultTextFormat
    ? createBlockNode(options?.defaultTextFormat, [createTextNode(line)])
    : createTextParagraph(line);
};

const isListItemText = (text: string) => {
  return /^\s*([0-9]+\.|-)\s*(.*)/.test(text);
};

const parseListItemText = (line: string) => {
  const match = line.match(/^\s*([0-9]+\.|-)\s*(.*)/);
  return match ? match[2] : line;
};

interface ParsedInstructionData {
  type: string;
  id?: string | null;
  instruction?: string | null;
}

const parseInstructionData = (el: HTMLElement): ParsedInstructionData | null => {
  if (!el.hasAttribute('data-prompt-instruction')) {
    return null;
  }

  const result: ParsedInstructionData = {
    type: el.getAttribute('data-instruction-type') || PROMPT_INSTRUCTION_TYPE.generic,
  };
  if (el.hasAttribute('data-id')) {
    result.id = el.getAttribute('data-id');
  }
  if (el.hasAttribute('data-instruction')) {
    result.instruction = el.getAttribute('data-instruction');
  }

  return result;
};
